import React from 'react';
import { Customized, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import {
  ChartTooltip,
  ChartTooltipContent,
  ChartContainer,
  ChartConfig
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';
export interface RadialData {
  name: string;
  value: number;
  color: string;
}

interface RadialProps {
  data: RadialData[];
  width?: string;
  height?: string;
  innerRadius?: number;
  outerRadius?: number;
  cx?: string;
  cy?: string;
  startAngle?: number;
  endAngle?: number;
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  dataKey?: string;
  nameKey?: string;
  showTooltip?: boolean;
  showBackground?: boolean;
  labelFormatter?: (label: any, payload: any) => React.ReactNode;
  legendClassName?: string;
  textX?: string;
  textY?: string;
  children?: React.ReactNode;
  customContent?: (props: any) => React.ReactElement;
}

const RadialChartComponent: React.FC<RadialProps> = ({
  data,
  width = '100%',
  height = '100%',
  innerRadius = 30,
  outerRadius = 110,
  cx = '50%',
  cy = '50%',
  startAngle = 90,
  endAngle = 450,
  showLegend = true,
  legendPosition = 'right',
  dataKey = 'value',
  nameKey = 'label',
  showTooltip = true,
  showBackground = true,
  labelFormatter,
  legendClassName = '',
  textX = '50%',
  textY = '50%',
  children,
  customContent
}) => {
  // 动态生成 chartConfig
  const chartConfig: ChartConfig = {
    ...data.reduce(
      (config, item) => {
        config[item.name] = {
          label: item.name.charAt(0).toUpperCase() + item.name.slice(1),
          color: item.color
        };
        return config;
      },
      {} as Record<string, { label: string; color: string }>
    )
  };

  // 转换数据格式以适配 RadialBarChart
  const chartData = data.map(item => ({
    [nameKey]: item.name,
    [dataKey]: item.value,
    fill: item.color
  }));

  const renderLegend = () => {
    if (!showLegend) return null;

    return (
      <div
        className={cn(
          `flex ${
            legendPosition === 'right'
              ? 'flex-col'
              : legendPosition === 'left'
                ? 'flex-col'
                : legendPosition === 'top'
                  ? 'flex-row'
                  : legendPosition === 'bottom'
                    ? 'absolute bottom-0 flex-row'
                    : 'flex-row'
          } gap-1`,
          legendClassName
        )}
      >
        {data.map((entry, index) => (
          <div key={`legend-${index}`} className="mr-1 flex items-center gap-1">
            <div className="h-2 w-2 rounded-sm" style={{ backgroundColor: entry.color }}></div>
            <span className="whitespace-nowrap text-xs text-[#1F1F1F]">{entry.name}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`relative flex h-full w-full items-center gap-4 ${legendPosition === 'bottom' ? 'flex-col' : ''}`}
    >
      <div style={{ width, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ChartContainer
            config={chartConfig}
            className={`aspect-square ${legendPosition === 'right' ? 'flex-1' : ''}`}
            style={{ maxHeight: height }}
          >
            <RadialBarChart
              data={chartData}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              startAngle={startAngle}
              endAngle={endAngle}
              cx={cx}
              cy={cy}
            >
              {showTooltip && (
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      hideLabel
                      nameKey={nameKey}
                      labelFormatter={labelFormatter}
                    />
                  }
                />
              )}
              <RadialBar dataKey={dataKey} background={showBackground} />
              {/* 自定义内容插槽 */}
              {customContent && <Customized component={customContent} />}
              {/* 子组件插槽 */}
              {children}
            </RadialBarChart>
          </ChartContainer>
        </ResponsiveContainer>
      </div>
      {renderLegend()}
    </div>
  );
};

export default RadialChartComponent;
